import React from 'react';
import { AlertTriangle, CheckCircle2, Download, LogOut, RefreshCw, ShieldCheck, WalletCards } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';
import { downloadPreparedFile } from '../utils/downloadFile';

const ADMIN_KEY_STORAGE = 'jpexgo_admin_key_v1';
const STAFF_SESSION_STORAGE = 'jpexgo_staff_session_v1';
const PAGE_SIZE = 20;

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const today = () => new Date().toISOString().slice(0, 10);

const formatGroupedNumber = (value, decimals = 2) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0.00';
  const fixed = n.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${grouped}.${decPart}` : grouped;
};

const formatMoney = (value) => `${formatGroupedNumber(value, 2)} сом`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({ label, value, hint, tone = 'default' }) => {
  const tones = {
    default: 'border-stroke bg-white/5',
    yellow: 'border-neon-yellow/25 bg-neon-yellow/10',
    green: 'border-emerald-400/25 bg-emerald-400/10',
    red: 'border-red-400/25 bg-red-400/10',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.default}`}>
      <div className="text-[11px] uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
      {hint ? <div className="mt-1 text-xs text-text-secondary">{hint}</div> : null}
    </div>
  );
};

const Pagination = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  const from = total ? page * pageSize + 1 : 0;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-stroke/70 pt-3 text-xs text-text-secondary sm:flex-row sm:items-center sm:justify-between">
      <div>
        Показано {from}-{to} из {total}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page <= 0}
          className="h-9 rounded-xl border border-stroke bg-white/5 px-3 font-semibold text-text-primary disabled:opacity-40"
        >
          Назад
        </button>
        <span>
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="h-9 rounded-xl border border-stroke bg-white/5 px-3 font-semibold text-text-primary disabled:opacity-40"
        >
          Вперёд
        </button>
      </div>
    </div>
  );
};

const ManagerLogin = ({ value, password, onChange, onPasswordChange, onSubmit, error, loading }) => (
  <div className="min-h-screen bg-bg px-4 py-8 text-text-primary">
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center">
      <div className="w-full rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
        <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
          <ShieldCheck size={15} /> Кабинет менеджера
        </div>
        <h1 className="mt-3 text-2xl font-black">Вход в сверку</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Введите логин и пароль сотрудника. Старый ADMIN_KEY временно тоже работает.
        </p>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="Логин или ADMIN_KEY"
          className="mt-5 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        <input
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Пароль сотрудника"
          type="password"
          className="mt-3 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value || loading}
          className="mt-4 h-12 w-full rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60"
        >
          Войти
        </button>
      </div>
    </div>
  </div>
);

const ManagerReconciliationPage = () => {
  const { initData, tg } = useTelegram();
  const initDataRaw = initData?.initData || '';
  const [adminKey, setAdminKey] = React.useState(() => {
    try {
      return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
    } catch (_) {
      return '';
    }
  });
  const [staffSessionToken, setStaffSessionToken] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STAFF_SESSION_STORAGE) || '{}')?.staffSessionToken || '';
    } catch (_) {
      return '';
    }
  });
  const [loginKey, setLoginKey] = React.useState(adminKey);
  const [loginPassword, setLoginPassword] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState(today());
  const [dateTo, setDateTo] = React.useState(today());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [bankPage, setBankPage] = React.useState(0);

  const hasTelegramAuth = typeof initDataRaw === 'string' && initDataRaw.includes('hash=');
  const hasAuth = Boolean(hasTelegramAuth || adminKey || staffSessionToken);
  const authBody = React.useCallback((extra = {}) => {
    if (staffSessionToken) return { staffSessionToken, ...extra };
    return hasTelegramAuth ? { initData: initDataRaw, ...extra } : { adminKey, ...extra };
  }, [adminKey, hasTelegramAuth, initDataRaw, staffSessionToken]);

  const loadReconciliation = React.useCallback(async () => {
    if (!hasAuth) return;
    setLoading(true);
    setError('');
    try {
      const r = await apiPost('/api/manager/reconciliation/summary', authBody({ dateFrom, dateTo }));
      setData(r);
      if (r?.filters?.dateFrom && r.filters.dateFrom !== dateFrom) setDateFrom(r.filters.dateFrom);
      if (r?.filters?.dateTo && r.filters.dateTo !== dateTo) setDateTo(r.filters.dateTo);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить сверку');
      if (!hasTelegramAuth && !staffSessionToken) {
        try {
          localStorage.removeItem(ADMIN_KEY_STORAGE);
        } catch (_) {
          // ignore
        }
        setAdminKey('');
      }
      if (staffSessionToken) {
        try {
          localStorage.removeItem(STAFF_SESSION_STORAGE);
        } catch (_) {
          // ignore
        }
        setStaffSessionToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [authBody, dateFrom, dateTo, hasAuth, hasTelegramAuth, staffSessionToken]);

  React.useEffect(() => {
    loadReconciliation();
  }, [loadReconciliation]);

  const login = async () => {
    const nextKey = String(loginKey || '').trim();
    if (!nextKey) return;
    setLoginLoading(true);
    setError('');
    try {
      let auth = { adminKey: nextKey };
      if (loginPassword) {
        const session = await apiPost('/api/staff/login', { login: nextKey, password: loginPassword });
        auth = { staffSessionToken: session.staffSessionToken };
        localStorage.setItem(STAFF_SESSION_STORAGE, JSON.stringify(session));
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setStaffSessionToken(session.staffSessionToken || '');
      } else {
        localStorage.setItem(ADMIN_KEY_STORAGE, nextKey);
      }
      const r = await apiPost('/api/manager/reconciliation/summary', { ...auth, dateFrom, dateTo });
      setData(r);
      if (r?.filters?.dateFrom && r.filters.dateFrom !== dateFrom) setDateFrom(r.filters.dateFrom);
      if (r?.filters?.dateTo && r.filters.dateTo !== dateTo) setDateTo(r.filters.dateTo);
      setAdminKey(loginPassword ? '' : nextKey);
      setLoginPassword('');
    } catch (_) {
      try {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        localStorage.removeItem(STAFF_SESSION_STORAGE);
      } catch (__) {
        // ignore
      }
      setAdminKey('');
      setStaffSessionToken('');
      setError('Неверный логин, пароль или ключ администратора');
    } finally {
      setLoginLoading(false);
    }
  };

  const exportReconciliation = async () => {
    setExportLoading(true);
    setError('');
    try {
      const res = await fetch(getApiPath('/api/manager/reconciliation/export/excel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authBody({ dateFrom, dateTo, prepareDownload: true })),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `Request failed: ${res.status}`);
      }
      const prepared = await res.json();
      await downloadPreparedFile(tg, prepared, `optima_reconciliation_${dateFrom}_${dateTo}.xlsx`);
    } catch (e) {
      setError(e?.message || 'Не удалось скачать Excel сверки');
    } finally {
      setExportLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
      localStorage.removeItem(STAFF_SESSION_STORAGE);
    } catch (_) {
      // ignore
    }
    setAdminKey('');
    setStaffSessionToken('');
    setLoginKey('');
    setLoginPassword('');
    setData(null);
  };

  React.useEffect(() => {
    setPage(0);
    setBankPage(0);
  }, [dateFrom, dateTo]);

  if (!hasAuth) {
    return <ManagerLogin value={loginKey} password={loginPassword} onChange={setLoginKey} onPasswordChange={setLoginPassword} onSubmit={login} error={error} loading={loginLoading} />;
  }

  const summary = data?.local?.summary || {};
  const payments = Array.isArray(data?.local?.payments) ? data.local.payments : [];
  const bankSummary = data?.bank?.summary || {};
  const bankRecords = Array.isArray(data?.bank?.records) ? data.bank.records : [];
  const warnings = Array.isArray(data?.local?.warnings) ? data.local.warnings : [];
  const differences = Array.isArray(data?.differences) ? data.differences : [];
  const missingEnv = data?.statementConfig?.missing || [];
  const isReady = Boolean(data?.statementConfig?.ready);
  const bankLoaded = Boolean(data?.bank?.loaded);
  const reconciliationStartDate = data?.filters?.reconciliationStartDate || '';
  const pagedPayments = payments.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pagedBankRecords = bankRecords.slice(bankPage * PAGE_SIZE, bankPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="min-h-screen bg-bg px-4 py-5 text-text-primary">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
                <WalletCards size={15} /> Банковская сверка
              </div>
              <h1 className="mt-3 text-2xl font-black tracking-tight">Сверка с Optima</h1>
              <p className="mt-1 max-w-2xl text-sm text-text-secondary">
                Сравниваем наши QR-оплаты с QR-выпиской Optima за выбранный период. Период выписки банка не должен превышать 14 дней.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={loadReconciliation} disabled={loading} className="inline-flex h-10 items-center gap-2 rounded-xl border border-neon-yellow/30 bg-neon-yellow/10 px-3 text-sm font-semibold text-neon-yellow disabled:opacity-60">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Обновить
              </button>
              <button type="button" onClick={exportReconciliation} disabled={loading || exportLoading} className="inline-flex h-10 items-center gap-2 rounded-xl border border-stroke bg-white/5 px-3 text-sm font-semibold disabled:opacity-50">
                <Download size={16} /> {exportLoading ? 'Формируем Excel...' : 'Скачать Excel'}
              </button>
              {!hasTelegramAuth ? (
                <button type="button" onClick={logout} className="inline-flex h-10 items-center gap-2 rounded-xl border border-stroke bg-white/5 px-3 text-sm font-semibold">
                  <LogOut size={16} /> Выйти
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <label className="block">
              <span className="text-xs text-text-secondary">С даты</span>
              <input type="date" min={reconciliationStartDate || undefined} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <label className="block">
              <span className="text-xs text-text-secondary">По дату</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={loadReconciliation} disabled={loading} className="h-11 rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60">Показать</button>
            </div>
          </div>

          {reconciliationStartDate ? (
            <p className="mt-3 text-xs text-text-secondary">
              Промышленная сверка ведётся с {new Date(`${reconciliationStartDate}T00:00:00`).toLocaleDateString('ru-RU')}. Более ранние операции относятся к тестированию до запуска v2.
            </p>
          ) : null}

          {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <StatCard label="QR-оплат в базе" value={summary.payments_count || 0} hint={formatMoney(summary.total_som)} tone="green" />
          <StatCard label="С transaction ID" value={summary.with_transaction_id_count || 0} hint="Можно сверять по ID" />
          <StatCard label="Без transaction ID" value={summary.without_transaction_id_count || 0} hint="Требуют внимания" tone={Number(summary.without_transaction_id_count || 0) ? 'yellow' : 'default'} />
          <StatCard label="Выписка банка" value={bankLoaded ? (bankSummary.totalOperationsCount || bankRecords.length || 0) : (isReady ? 'Ошибка' : 'Ждём')} hint={bankLoaded ? formatMoney(bankSummary.operationsTotalSum ?? bankSummary.totalSum) : (isReady ? (data?.bank?.error || 'Не загрузилась') : 'Нужны env-поля')} tone={bankLoaded ? 'green' : 'yellow'} />
        </div>

        {!isReady ? (
          <section className="mt-4 rounded-3xl border border-neon-yellow/25 bg-neon-yellow/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 shrink-0 text-neon-yellow" size={22} />
              <div>
                <h2 className="text-lg font-black text-neon-yellow">Нужно заполнить env для выписки</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  По документации Optima используем QR-выписку: /api/v1/get-qr-operation-statement.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {missingEnv.map((name) => (
                    <span key={name} className="rounded-full border border-neon-yellow/25 bg-black/20 px-3 py-1 text-xs font-semibold text-neon-yellow">{name}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="mt-4 rounded-3xl border border-emerald-400/25 bg-emerald-400/10 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-300" size={22} />
              <div>
                <h2 className="text-lg font-black text-emerald-100">{bankLoaded ? 'Выписка банка загружена' : 'Параметры выписки заполнены'}</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  {bankLoaded
                    ? `Период банка: ${bankSummary.statementDuration || '-'}, валюта: ${bankSummary.currency || '-'}, комиссия: ${formatMoney(bankSummary.totalCommission)}.`
                    : (data?.bank?.error || 'Backend попробовал загрузить выписку, но банк вернул ошибку или пустой ответ.')}
                </p>
              </div>
            </div>
          </section>
        )}

        {bankLoaded ? (
          <section className="mt-4 rounded-3xl border border-stroke bg-surface p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-black">Выписка Optima по QR</h2>
                <div className="mt-1 text-xs text-text-secondary">
                  {bankSummary.companyName || 'Компания'} · {bankSummary.statementDuration || 'период не указан'}
                </div>
              </div>
              <span className="text-xs text-text-secondary">{bankRecords.length} записей</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-text-muted">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Дата операции</th>
                    <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                    <th className="whitespace-nowrap px-3 py-2">Комиссия</th>
                    <th className="whitespace-nowrap px-3 py-2">Зачисление</th>
                    <th className="whitespace-nowrap px-3 py-2">STAN / ID</th>
                    <th className="whitespace-nowrap px-3 py-2">Описание</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBankRecords.map((p, index) => (
                    <tr key={`${p.transactionId || p.stan || index}-${p.paidAt || index}`} className="border-t border-stroke/70">
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatDateTime(p.paidAt)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-semibold">{formatMoney(p.amountSom)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatMoney(p.operationFeeSum)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatMoney(p.operationTransferSum)}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono">{p.transactionId || p.stan || '-'}</td>
                      <td className="max-w-[420px] truncate px-3 py-2 text-text-secondary">{p.operationName || p.description || '-'}</td>
                    </tr>
                  ))}
                  {!bankRecords.length ? <tr><td colSpan="6" className="px-3 py-8 text-center text-text-secondary">В выписке банка QR-операций за период нет.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <Pagination page={bankPage} total={bankRecords.length} pageSize={PAGE_SIZE} onPageChange={setBankPage} />
          </section>
        ) : null}

        {differences.length || warnings.length ? (
          <section className="mt-4 rounded-3xl border border-stroke bg-surface p-4">
            <h2 className="text-lg font-black">Замечания сверки</h2>
            <div className="mt-3 space-y-2">
              {[...differences, ...warnings].map((item, index) => (
                <div key={`${item.type || 'warning'}-${index}`} className="rounded-2xl border border-neon-yellow/20 bg-neon-yellow/10 p-3 text-sm">
                  <div className="font-semibold text-neon-yellow">{item.type || 'WARNING'}</div>
                  <div className="mt-1 text-text-secondary">{item.message}</div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-4 rounded-3xl border border-stroke bg-surface p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-black">Наши QR-оплаты за период</h2>
            <span className="text-xs text-text-secondary">{payments.length} записей</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-text-muted">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2">Дата оплаты</th>
                  <th className="whitespace-nowrap px-3 py-2">Сумма</th>
                  <th className="whitespace-nowrap px-3 py-2">Transaction ID</th>
                  <th className="whitespace-nowrap px-3 py-2">ФИО</th>
                  <th className="whitespace-nowrap px-3 py-2">Треки</th>
                  <th className="whitespace-nowrap px-3 py-2">Страна</th>
                </tr>
              </thead>
              <tbody>
                {pagedPayments.map((p) => (
                  <tr key={p.id} className="border-t border-stroke/70">
                    <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{formatDateTime(p.paid_at)}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-semibold">{formatMoney(p.amount_som)}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono">{p.bank_transaction_id || '-'}</td>
                    <td className="max-w-[280px] truncate px-3 py-2 text-text-secondary">{p.recipient_names || '-'}</td>
                    <td className="max-w-[320px] truncate px-3 py-2 text-text-secondary">{p.track_numbers || '-'}</td>
                    <td className="whitespace-nowrap px-3 py-2">{p.countries || '-'}</td>
                  </tr>
                ))}
                {!payments.length ? <tr><td colSpan="6" className="px-3 py-8 text-center text-text-secondary">QR-оплат за период нет.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={payments.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </section>
      </div>
    </div>
  );
};

export default ManagerReconciliationPage;
